<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { CalculationMode } from '@fire-calc/domain-ts';
import { stripCzkShortUnit, splitCzkShortSuffix } from '@fire-calc/ui-vue';
import BasicInfoInputCard from '../components/BasicInfoInputCard.vue';
import SimulationParametersAccordion from '../components/SimulationParametersAccordion.vue';
import EventListCard from '../components/EventListCard.vue';
import HeroBanner from '../components/HeroBanner.vue';
import MobileFloatingResults from '../components/MobileFloatingResults.vue';
import MobileResultsBottomSheet from '../components/MobileResultsBottomSheet.vue';
import { NavPanel } from '@fire-calc/ui-vue';
import { useCalculatorStore } from '../state/calculatorStore';
import { strategyFormatCzkShort } from '../utils/format';

const store = useCalculatorStore();
const { planResult, scenario, mode } = storeToRefs(store);
const { t } = useI18n();

const sheetOpen = ref(false);

const classicalValue = computed<{ number: string; suffix: string }>(() => {
  if (!planResult.value?.classicalFire) return { number: '—', suffix: '' };
  if (mode.value === CalculationMode.byRetirementAge) {
    return splitCzkShortSuffix(
      stripCzkShortUnit(
        strategyFormatCzkShort(planResult.value.classicalFire.requiredMonthlyInvestment.value),
      ),
    );
  }
  return {
    number: planResult.value.classicalFire.solvedRetirementAge?.value?.toString() ?? '—',
    suffix: '',
  };
});

const dwzValue = computed<{ number: string; suffix: string }>(() => {
  if (!planResult.value?.dieWithZero) return { number: '—', suffix: '' };
  if (mode.value === CalculationMode.byRetirementAge) {
    return splitCzkShortSuffix(
      stripCzkShortUnit(
        strategyFormatCzkShort(planResult.value.dieWithZero.requiredMonthlyInvestment.value),
      ),
    );
  }
  return {
    number: planResult.value.dieWithZero.solvedRetirementAge?.value?.toString() ?? '—',
    suffix: '',
  };
});

const prose = computed(() => {
  if (!planResult.value) return null;
  if (mode.value === CalculationMode.byRetirementAge) {
    if (!scenario.value.retirementAge.value) return null;
    return t('floatingBarRequiredInvestmentLine', { age: scenario.value.retirementAge.value });
  }
  return t('floatingBarSolvedRetirementAgeLine');
});

function commitClose(): void {
  sheetOpen.value = false;
}

const rows = computed(() => {
  const r = planResult.value;
  if (!r) return [];
  const survivalClassical = t('floatingBarSurvivalSwr', {
    swr: scenario.value.swr.asPercent.toFixed(1).replace('.', ','),
  });
  const survivalDwz = t('floatingBarSurvivalUntil90');
  if (mode.value === CalculationMode.byRetirementAge) {
    const pmClass = r.classicalFire?.requiredMonthlyInvestment.value;
    const pmDwz = r.dieWithZero?.requiredMonthlyInvestment.value;
    return [
      {
        label: t('floatingBarRequiredInvestmentLine', { age: scenario.value.retirementAge.value }),
        classicalValue: pmClass ? strategyFormatCzkShort(pmClass) : '—',
        dwzValue: pmDwz ? strategyFormatCzkShort(pmDwz) : '—',
      },
      {
        label: t('sheetTargetPortfolioInTodaysPrices'),
        classicalValue: r.classicalFire
          ? strategyFormatCzkShort(r.classicalFire.fireNumber.value)
          : '—',
        dwzValue: r.dieWithZero
          ? strategyFormatCzkShort(r.dieWithZero.fireNumber.value)
          : '—',
      },
      {
        label: t('resultPortfolioDuration'),
        classicalValue: survivalClassical,
        dwzValue: survivalDwz,
      },
    ];
  }
  return [
    {
      label: t('sheetAgeOfFinancialFreedom'),
      classicalValue: r.classicalFire?.solvedRetirementAge?.value
        ? `${r.classicalFire.solvedRetirementAge.value} ${t('heroUnitYears')}`
        : '—',
      dwzValue: r.dieWithZero?.solvedRetirementAge?.value
        ? `${r.dieWithZero.solvedRetirementAge.value} ${t('heroUnitYears')}`
        : '—',
    },
    {
      label: t('sheetTargetPortfolioInTodaysPrices'),
      classicalValue: r.classicalFire
        ? strategyFormatCzkShort(r.classicalFire.fireNumber.value)
        : '—',
      dwzValue: r.dieWithZero
        ? strategyFormatCzkShort(r.dieWithZero.fireNumber.value)
        : '—',
    },
    {
      label: t('resultPortfolioDuration'),
      classicalValue: survivalClassical,
      dwzValue: survivalDwz,
    },
  ];
});
</script>

<template>
  <div class="min-h-screen bg-bg-page pb-48">
    <div class="mx-auto max-w-content w-full p-lg">
      <NavPanel :title="t('appTitle')" />
      <div class="mt-xl"><HeroBanner /></div>
      <div class="mt-3xl flex flex-col gap-lg">
        <BasicInfoInputCard />
        <SimulationParametersAccordion />
        <EventListCard
          :events="scenario.events"
          :current-age="scenario.currentAge.value"
          :current-age-year="new Date().getFullYear()"
          :store="store"
        />
      </div>
    </div>
    <MobileFloatingResults
      v-show="!sheetOpen"
      :classical-value="classicalValue.number"
      :classical-suffix="classicalValue.suffix"
      :dwz-value="dwzValue.number"
      :dwz-suffix="dwzValue.suffix"
      :prose="prose"
      :mode="mode"
      @tap="sheetOpen = true"
    />
    <MobileResultsBottomSheet
      :open="sheetOpen"
      :header="prose ?? t('resultComparisonTitle')"
      :classical-label="t('strategyClassical')"
      :dwz-label="t('strategyDwz')"
      :rows="rows"
      @close="commitClose"
    />
  </div>
</template>